/**
 * Shared draw core: renders one frame of an edit at time t onto a 2d context.
 * Used unchanged by the live preview AND the exporter. Pure — media elements
 * are supplied by a MediaProvider; transition math is exported for tests.
 */
import {
  activeClipsAt,
  audibleTracks,
  clipDuration,
  clipEnd,
  effectsToFilterString,
  fadeFactor,
  transitionWindowAt,
} from '../format/helpers';
import { topmostOpaqueIndex } from '../format/occlusion';
import { sampleEffects, sampleParam, sampleTransform } from '../format/params';
import type {
  Edit,
  ImageClip,
  ResolvedTransform,
  TextClip,
  TransitionType,
  VideoClip,
} from '../format/types';

/** Anything drawImage accepts that we use, or null when not ready. */
export type Drawable =
  | HTMLVideoElement
  | HTMLImageElement
  | HTMLCanvasElement
  | ImageBitmap
  | OffscreenCanvas
  | VideoFrame;

/**
 * Supplies the drawable for a source. The optional clipId lets providers hold
 * one decoded frame PER CLIP (the same source can sit on two tracks at two
 * different times); providers that ignore it fall back to one frame per source.
 */
export type MediaProvider = (sourceId: string, clipId?: string) => Drawable | null;

/**
 * Structural subset of CanvasRenderingContext2D used by the compositor —
 * lets tests inject a recording fake without a DOM.
 */
export interface Ctx2D {
  fillStyle: string | CanvasGradient | CanvasPattern;
  strokeStyle: string | CanvasGradient | CanvasPattern;
  globalAlpha: number;
  globalCompositeOperation: string;
  filter: string;
  font: string;
  textAlign: CanvasTextAlign;
  textBaseline: CanvasTextBaseline;
  lineWidth: number;
  lineJoin: CanvasLineJoin;
  save(): void;
  restore(): void;
  beginPath(): void;
  rect(x: number, y: number, w: number, h: number): void;
  clip(): void;
  translate(x: number, y: number): void;
  rotate(rad: number): void;
  scale(x: number, y: number): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  drawImage(image: Drawable, dx: number, dy: number, dw: number, dh: number): void;
  fillText(text: string, x: number, y: number): void;
  strokeText(text: string, x: number, y: number): void;
  measureText(text: string): { width: number };
}

// ---------------------------------------------------------------------------
// Transition math (pure, tested)
// ---------------------------------------------------------------------------

export interface TransitionRender {
  /** draw outgoing clip A at all? */
  drawA: boolean;
  drawB: boolean;
  aAlpha: number;
  bAlpha: number;
  /** pixel offsets for slide transitions */
  aOffset: [number, number];
  bOffset: [number, number];
  /** clip rect [x, y, w, h] applied when drawing B (wipes) */
  bClipRect: [number, number, number, number] | null;
  /** full-frame color overlay (fadeblack/fadewhite), alpha 0..1 */
  overlayColor: string | null;
  overlayAlpha: number;
}

const NO_OFFSET: [number, number] = [0, 0];

export function transitionRenderParams(
  type: TransitionType,
  progress: number,
  w: number,
  h: number,
): TransitionRender {
  const p = Math.max(0, Math.min(1, progress));
  const base: TransitionRender = {
    drawA: true,
    drawB: true,
    aAlpha: 1,
    bAlpha: 1,
    aOffset: NO_OFFSET,
    bOffset: NO_OFFSET,
    bClipRect: null,
    overlayColor: null,
    overlayAlpha: 0,
  };
  switch (type) {
    case 'crossfade':
      return { ...base, bAlpha: p };
    case 'fadeblack':
    case 'fadewhite': {
      const color = type === 'fadeblack' ? '#000000' : '#ffffff';
      if (p < 0.5)
        return { ...base, drawB: false, overlayColor: color, overlayAlpha: p * 2 };
      return { ...base, drawA: false, overlayColor: color, overlayAlpha: (1 - p) * 2 };
    }
    case 'wipeleft':
      // B revealed from the right edge, boundary sweeping left
      return { ...base, bClipRect: [w * (1 - p), 0, w * p, h] };
    case 'wiperight':
      return { ...base, bClipRect: [0, 0, w * p, h] };
    case 'wipeup':
      return { ...base, bClipRect: [0, h * (1 - p), w, h * p] };
    case 'wipedown':
      return { ...base, bClipRect: [0, 0, w, h * p] };
    case 'slideleft':
      // both slide leftward; B enters from the right
      return { ...base, aOffset: [-w * p, 0], bOffset: [w * (1 - p), 0] };
    case 'slideright':
      return { ...base, aOffset: [w * p, 0], bOffset: [-w * (1 - p), 0] };
  }
}

/**
 * Letterbox fit: destination rect (centered) for a source of sw×sh inside a
 * frame of w×h, preserving aspect ratio.
 */
export function fitRect(
  sw: number,
  sh: number,
  w: number,
  h: number,
): [number, number, number, number] {
  if (sw <= 0 || sh <= 0) return [0, 0, w, h];
  const scale = Math.min(w / sw, h / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  return [(w - dw) / 2, (h - dh) / 2, dw, dh];
}

/** Equal-power crossfade gains for progress p: [gainOut, gainIn]. */
export function equalPowerGains(p: number): [number, number] {
  const c = Math.max(0, Math.min(1, p));
  return [Math.cos((c * Math.PI) / 2), Math.sin((c * Math.PI) / 2)];
}

// ---------------------------------------------------------------------------
// Frame drawing
// ---------------------------------------------------------------------------

function drawableSize(d: Drawable): [number, number] {
  if (typeof HTMLVideoElement !== 'undefined' && d instanceof HTMLVideoElement)
    return [d.videoWidth, d.videoHeight];
  if (typeof VideoFrame !== 'undefined' && d instanceof VideoFrame)
    return [d.displayWidth, d.displayHeight];
  const any = d as { width: number; height: number };
  return [any.width, any.height];
}

interface VideoDrawOpts {
  alpha: number;
  offset: [number, number];
  clipRect: [number, number, number, number] | null;
  /** timeline time of the frame — resolved to source time for param sampling */
  t: number;
}

/** Source time of a video clip at timeline time t (keyframe sampling axis). */
export function sourceTimeAt(clip: VideoClip, t: number): number {
  return clip.in + (t - clip.start) * clip.speed;
}

function drawVideoClip(
  ctx: Ctx2D,
  clip: VideoClip,
  drawable: Drawable | null,
  w: number,
  h: number,
  opts: VideoDrawOpts,
): void {
  const sampleT = sourceTimeAt(clip, opts.t);
  const transform = sampleTransform(clip.transform, sampleT);
  const alpha = opts.alpha * transform.opacity;
  if (alpha <= 0 || !drawable) return;
  const [sw, sh] = drawableSize(drawable);
  if (sw <= 0 || sh <= 0) return;
  ctx.save();
  if (opts.clipRect) {
    ctx.beginPath();
    ctx.rect(...opts.clipRect);
    ctx.clip();
  }
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  ctx.filter = effectsToFilterString(sampleEffects(clip.effects, sampleT));
  applyTransform(ctx, transform, w, h, opts.offset);
  const [dx, dy, dw, dh] = fitRect(sw, sh, w, h);
  // after transform, draw centered around origin
  ctx.drawImage(drawable, dx - w / 2, dy - h / 2, dw, dh);
  ctx.restore();
}

function applyTransform(
  ctx: Ctx2D,
  t: ResolvedTransform,
  w: number,
  h: number,
  extraOffset: [number, number],
): void {
  // transform origin: frame center, then normalized x/y offset of clip center
  ctx.translate(
    w / 2 + t.x * w + extraOffset[0],
    h / 2 + t.y * h + extraOffset[1],
  );
  if (t.rotate) ctx.rotate((t.rotate * Math.PI) / 180);
  if (t.scale !== 1) ctx.scale(t.scale, t.scale);
}

function drawImageClip(
  ctx: Ctx2D,
  clip: ImageClip,
  drawable: Drawable | null,
  w: number,
  h: number,
  t: number,
): void {
  if (!drawable) return;
  const transform = sampleTransform(clip.transform, t - clip.start);
  const alpha = transform.opacity * fadeFactor(clip, clip.duration, t);
  if (alpha <= 0) return;
  const [sw, sh] = drawableSize(drawable);
  if (sw <= 0 || sh <= 0) return;
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  ctx.filter = 'none';
  applyTransform(ctx, transform, w, h, NO_OFFSET);
  const [dx, dy, dw, dh] = fitRect(sw, sh, w, h);
  ctx.drawImage(drawable, dx - w / 2, dy - h / 2, dw, dh);
  ctx.restore();
}

const FONT_STACKS: Record<TextClip['style']['fontFamily'], string> = {
  sans: 'Helvetica, Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  mono: 'Menlo, Consolas, monospace',
};

export function drawTextClip(
  ctx: Ctx2D,
  clip: TextClip,
  w: number,
  h: number,
  t: number,
): void {
  const alpha = fadeFactor(clip, clip.duration, t);
  if (alpha <= 0) return;
  const s = clip.style;
  const fontSize = Math.max(1, s.fontSize * h);
  const lineHeight = fontSize * 1.25;
  const lines = clip.text.split('\n');
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  ctx.filter = 'none';
  ctx.font = `${s.italic ? 'italic ' : ''}${s.bold ? 'bold ' : ''}${fontSize}px ${FONT_STACKS[s.fontFamily]}`;
  ctx.textBaseline = 'middle';

  let maxWidth = 0;
  for (const line of lines) maxWidth = Math.max(maxWidth, ctx.measureText(line).width);
  const pad = fontSize * 0.4;
  const boxW = maxWidth + pad * 2;
  const boxH = lines.length * lineHeight + pad * 2;
  const local = t - clip.start;
  const cx = sampleParam(clip.position.x, local) * w;
  const cy = sampleParam(clip.position.y, local) * h;

  if (s.background) {
    ctx.fillStyle = s.background;
    ctx.fillRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH);
  }

  ctx.textAlign = s.align;
  const textX =
    s.align === 'left'
      ? cx - maxWidth / 2
      : s.align === 'right'
        ? cx + maxWidth / 2
        : cx;
  const firstY = cy - ((lines.length - 1) * lineHeight) / 2;

  for (let i = 0; i < lines.length; i++) {
    const y = firstY + i * lineHeight;
    if (s.outline && s.outlineWidth > 0) {
      ctx.strokeStyle = s.outline;
      ctx.lineWidth = Math.max(0.5, s.outlineWidth * h * 2);
      ctx.lineJoin = 'round';
      ctx.strokeText(lines[i], textX, y);
    }
    ctx.fillStyle = s.color;
    ctx.fillText(lines[i], textX, y);
  }
  ctx.restore();
}

/**
 * Draw the complete frame at time t.
 * Track order: video tracks bottom-up, then overlay tracks on top.
 */
export function drawFrame(
  ctx: Ctx2D,
  edit: Edit,
  t: number,
  media: MediaProvider,
): void {
  const { width: w, height: h, background } = edit.settings;
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.filter = 'none';
  ctx.fillStyle = background || '#000000';
  ctx.fillRect(0, 0, w, h);
  ctx.restore();

  // Skip video tracks fully occluded by an opaque full-frame clip above them.
  // Conservative: only when the occluder's drawable is ready and its aspect
  // matches the frame (no letterbox bars for lower tracks to show through).
  const occluder = topmostOpaqueIndex(edit, t, (clip) => {
    const d = media(clip.source, clip.id);
    if (!d) return false;
    const [sw, sh] = drawableSize(d);
    if (sw <= 0 || sh <= 0) return false;
    return Math.abs(sw / sh - w / h) < 0.02;
  });

  for (let ti = 0; ti < edit.tracks.length; ti++) {
    const track = edit.tracks[ti];
    if (track.kind !== 'video') continue;
    if (ti < occluder) continue;
    // track-wide blend + opacity: every draw of this track composites with
    // the track's blend mode and carries its opacity multiplier
    const trackAlpha = track.opacity ?? 1;
    if (trackAlpha <= 0) continue;
    ctx.globalCompositeOperation = track.blend ?? 'source-over';
    const window = transitionWindowAt(track, t);
    if (window) {
      const params = transitionRenderParams(window.type, window.progress, w, h);
      if (params.drawA) {
        const a = window.a;
        drawVideoClip(ctx, a, media(a.source, a.id), w, h, {
          alpha: trackAlpha * params.aAlpha * fadeFactor(a, clipDuration(a), t),
          offset: params.aOffset,
          clipRect: null,
          t,
        });
      }
      if (params.drawB) {
        const b = window.b;
        drawVideoClip(ctx, b, media(b.source, b.id), w, h, {
          alpha: trackAlpha * params.bAlpha * fadeFactor(b, clipDuration(b), t),
          offset: params.bOffset,
          clipRect: params.bClipRect,
          t,
        });
      }
      if (params.overlayColor && params.overlayAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = Math.min(1, trackAlpha * params.overlayAlpha);
        ctx.filter = 'none';
        ctx.fillStyle = params.overlayColor;
        ctx.fillRect(0, 0, w, h);
        ctx.restore();
      }
    } else {
      for (const clip of activeClipsAt(track, t)) {
        drawVideoClip(ctx, clip, media(clip.source, clip.id), w, h, {
          alpha: trackAlpha * fadeFactor(clip, clipDuration(clip), t),
          offset: NO_OFFSET,
          clipRect: null,
          t,
        });
      }
    }
    ctx.globalCompositeOperation = 'source-over';
  }

  for (const track of edit.tracks) {
    if (track.kind !== 'overlay') continue;
    for (const clip of activeClipsAt(track, t)) {
      if (clip.kind === 'text') drawTextClip(ctx, clip, w, h, t);
      else drawImageClip(ctx, clip, media(clip.source, clip.id), w, h, t);
    }
  }
}

/**
 * Audio gain for a clip at time t including fades and transition crossfade
 * (equal-power over the overlap window). Pure — used by preview and export.
 * Mute/solo semantics live in `audibleTracks` (solo affects audio only).
 */
export function clipAudioGainAt(
  edit: Edit,
  trackIndex: number,
  clip: VideoClip,
  t: number,
): number {
  const track = edit.tracks[trackIndex];
  if (!track || track.kind !== 'video' || !audibleTracks(edit).has(track.id)) return 0;
  if (clip.muted || clip.volume <= 0) return 0;
  const dur = clipDuration(clip);
  if (t < clip.start || t >= clip.start + dur) return 0;
  let gain = clip.volume * fadeFactor(clip, dur, t);
  const window = transitionWindowAt(track, t);
  if (window) {
    const [gOut, gIn] = equalPowerGains(window.progress);
    if (window.a.id === clip.id) gain *= gOut;
    else if (window.b.id === clip.id) gain *= gIn;
  }
  return gain;
}

/** Audio gain for an audio-track clip at time t (fades + track gain). */
export function audioClipGainAt(
  edit: Edit,
  trackIndex: number,
  clipId: string,
  t: number,
): number {
  const track = edit.tracks[trackIndex];
  if (!track || track.kind !== 'audio' || !audibleTracks(edit).has(track.id)) return 0;
  const clip = track.clips.find((c) => c.id === clipId);
  if (!clip) return 0;
  const dur = clipEnd(clip) - clip.start;
  if (t < clip.start || t >= clipEnd(clip)) return 0;
  return clip.gain * track.gain * fadeFactor(clip, dur, t);
}
